// /app/frontend/src/components/UserMenu.jsx
import React from 'react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { User, LogOut, CreditCard, Crown } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const UserMenu = ({ onUpgradeClick }) => {
  const { user, logout, isPro } = useAuth();

  const handleManageBilling = async () => {
    if (!user?.plan || user.plan === 'free') {
      onUpgradeClick?.();
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/billing/create-portal-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const { url } = await response.json();
        window.location.href = url;
      }
    } catch (error) {
      console.error('Portal error:', error);
    }
  };

  const getPlanLabel = () => {
    if (!user) return '';
    if (user.plan === 'pro' && user.subscriptionStatus === 'active') {
      return user.billingCycle === 'annual' ? 'Pro (Annual)' : 'Pro (Monthly)';
    }
    if (user.subscriptionStatus === 'past_due') {
      return 'Pro (Past Due)';
    }
    return 'Free';
  };

  if (!user) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2" data-testid="user-menu-trigger">
          <div className="h-7 w-7 rounded-full bg-[#00284F] flex items-center justify-center">
            <User className="h-4 w-4 text-white" />
          </div>
          <span className="hidden md:inline text-sm max-w-[120px] truncate">
            {user.email}
          </span>
          {isPro && (
            <Crown className="h-4 w-4 text-amber-500" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col gap-1">
            <p className="text-sm font-medium truncate">{user.email}</p>
            <Badge variant={isPro ? "default" : "secondary"} className="w-fit text-xs">
              {getPlanLabel()}
            </Badge>
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleManageBilling} data-testid="billing-menu-item">
          <CreditCard className="mr-2 h-4 w-4" />
          {isPro ? 'Manage Billing' : 'Upgrade to Pro'}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="logout-menu-item">
          <LogOut className="mr-2 h-4 w-4" />
          Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
